import { useSuspenseQuery } from '@tanstack/react-query';
import {
  fetchInternalOnboardingHome,
  type GetInternalOnboardingHomeResponse,
} from '@/apis/homeOnboardingApi';

// Split out of useHomePageData.ts: HomeRouter renders eagerly (not behind its
// own dynamic import), so anything it imports ships in the route's initial
// chunk. The other hooks in useHomePageData.ts back the member-view widgets,
// which HomeRouter only loads lazily via `dynamic()`, so sharing one file with
// them would pull that lazy-only code into the eager chunk too. The shared
// retry config lives in its own dependency-free module for the same reason.

import { HOME_QUERY_KEY, retryFn, retryDelay } from '@/hooks/homeQueryConfig';

// One-way latch: once server returns value=1, stays 1 forever for this org.
// useSuspenseQuery → parent <Suspense> holds the fallback until fetch resolves,
// so HomeRouter never has to paint an intermediate loading state.
// orgUUID is required; the caller must gate rendering on it being present.
//
// Failure handling: the queryFn catches all errors and resolves to
// `{ value: 0 }` so a transient 5xx on `/getInternalOnboardingHome/` doesn't
// bubble to the route-level error boundary. Previously, this query
// re-throwing rendered `app/(dashboard)/home/error.tsx` — "Try again" then
// refetched, hit the same backend hiccup, and looped (worst on Firefox where
// each error-boundary cycle re-mounts the heavy ViewTransition tree).
// Defaulting to `value: 0` routes the user to the onboarding branch on
// transient errors, which is the safer fallback for a brand-new account
// AND for a returning user who can simply reload once the backend recovers.
export function useHomeOnboardingStatus(orgUUID: string) {
  return useSuspenseQuery<GetInternalOnboardingHomeResponse>({
    queryKey: [HOME_QUERY_KEY, 'onboardingHome', orgUUID],
    queryFn: async (): Promise<GetInternalOnboardingHomeResponse> => {
      try {
        return await fetchInternalOnboardingHome(orgUUID);
      } catch (error) {
        console.warn(
          '[useHomeOnboardingStatus] fetch failed; defaulting to value=0',
          error
        );
        return { value: 0 };
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: retryFn,
    retryDelay,
    networkMode: 'online',
  });
}
